/**
 * Qué decir sobre las cuentas descubiertas — y, sobre todo, cuándo NO decir un número.
 *
 * Vive afuera del componente porque es exactamente donde estaba el defecto: la pantalla pintaba
 * «Encontramos {count}» con el total de cuentas apenas había una conexión, y las conexiones llegan
 * mucho antes que el padrón de cuentas (son dos pedidos, y el segundo el backend recién lo está
 * trayendo de la plataforma). En el medio el total es 0, así que la pantalla anunciaba «Encontramos
 * 0 cuentas» sobre una cuenta con 95 — Pablo reaccionó «no, imposible» antes de que se corrigiera
 * sola. «Cero» es la peor lectura posible de «todavía no terminé».
 *
 * No es un problema de Meta: la sección es del paso entero y `discoveredTotal` es el total de las
 * cuatro plataformas, así que el hueco existe cada vez que se conecta cualquiera de ellas.
 *
 * **Y el número grande cuenta las cuentas NO cerradas** (D7, card 2yINyKQv). Leo vio «Encontramos
 * 386 cuentas» con el MCC de Bakián: era el padrón entero, cerradas incluidas, y encima parcial. El
 * paso 3 después hablaba de «120 agrupadas», «23 para revisar» y «967 cerradas», y ninguna pantalla
 * decía el total (1.110). Las cerradas se dicen aparte, con su número, para que la suma cierre.
 */

import {FACEBOOK_CHANNEL, GOOGLE_CHANNEL, LINKEDIN_CHANNEL, TIKTOK_CHANNEL} from '../../channels/channels';
import {LIVENESS_DEAD} from '../wizardReducer';

/** Dónde trae `agency/connections/` las credenciales de cada plataforma del túnel. */
const CREDENTIAL_KEYS = {
    [GOOGLE_CHANNEL]: 'google_credentials',
    [FACEBOOK_CHANNEL]: 'facebook_credentials',
    [TIKTOK_CHANNEL]: 'tiktok_credentials',
    [LINKEDIN_CHANNEL]: 'linkedin_credentials',
};

export const PLATFORM_CHANNELS = Object.keys(CREDENTIAL_KEYS);

export function credentialsOf(connections, channel) {
    return connections?.[CREDENTIAL_KEYS[channel]] || [];
}

/**
 * Cuántas de las cuatro plataformas tienen al menos una credencial. Lo usa también la celebración
 * de la primera consulta («N plataformas conectadas»): los dos pasos cuentan con la misma regla.
 */
export function connectedPlatformsCountOf(connections) {
    return PLATFORM_CHANNELS.filter(channel => credentialsOf(connections, channel).length > 0).length;
}

export const COUNTING = 'COUNTING';
export const FOUND = 'FOUND';
export const NONE = 'NONE';
/**
 * Hay cuentas, pero el conteo todavía no terminó: alguna credencial sigue sincronizando o el padrón
 * está en vuelo. Es el estado en que Leo leyó «386» como final — el número seguía creciendo.
 */
export const STILL_SEARCHING = 'STILL_SEARCHING';

/**
 * Cerrada = la PLATAFORMA la reporta deshabilitada o cancelada. Es el mismo `liveness` con el que
 * el backend las aparta antes de agrupar (`global_line_candidates_service.py`) y el mismo con el que
 * el paso 3 las manda al cajón: contar con otra regla haría que los dos pasos no coincidan.
 *
 * Sin el dato (un backend que no lo mande) la cuenta cuenta como abierta: es el total de siempre.
 */
export function isClosedAccount(account) {
    return account?.liveness === LIVENESS_DEAD;
}

/**
 * Cubierta = ya está en una línea de seguimiento de la agencia. `reconcile` (`wizardReducer.js`) la
 * descarta antes de armar la propuesta, viva o cerrada, así que el paso 3 no la cuenta: si el paso 2
 * la contara, los dos números no cerrarían en una agencia que vuelve al túnel con líneas ya creadas
 * (review C-tunel-04). Por eso tampoco sirve el `dead_count` crudo del backend: incluye cubiertas.
 */
export function isCoveredAccount(account) {
    return Boolean(account?.covered);
}

/**
 * `{open, closed}` de lo que se puede proponer: el número grande y el que se dice aparte. Mismo
 * corte que el paso 3 (sin las cubiertas), para que `open + closed` sea su total.
 */
export function accountCountsOf(accounts) {
    const offerable = (accounts || []).filter(account => !isCoveredAccount(account));
    const closed = offerable.filter(isClosedAccount).length;
    return {open: offerable.length - closed, closed};
}

/**
 * En cuál de los cuatro estados está la sección de resultado.
 *
 * Se está CONTANDO mientras alguna credencial sincroniza, mientras el padrón está en vuelo, o
 * mientras nunca volvió (`candidates` es `undefined` hasta el primer fetch). Si el sync terminó y
 * el padrón llegó con cero, el cero es verdad — y ahí sí se dice, pero como lo que es: la cuenta
 * que se autorizó no administra ninguna cuenta publicitaria, no un hallazgo.
 *
 * Con cuentas y todavía contando, el número se dice pero NO como final (`STILL_SEARCHING`).
 */
export function resultStateOf({discoveredTotal, syncing, loadingCandidates, candidatesArrived}) {
    const stillCounting = syncing || loadingCandidates || !candidatesArrived;
    if (discoveredTotal > 0) return stillCounting ? STILL_SEARCHING : FOUND;
    if (stillCounting) return COUNTING;
    return NONE;
}

/**
 * El marco verde es una afirmación: sólo lo lleva el estado que de verdad encontró cuentas y
 * terminó de contarlas. Un número que todavía puede crecer va en el marco neutro.
 */
export function resultClassName(state) {
    if (state === COUNTING || state === STILL_SEARCHING) return 'ob-result -pending';
    if (state === NONE) return 'ob-result -empty';
    return 'ob-result';
}

export function resultTitleKey(state) {
    return state === COUNTING ? 'onboarding.platforms.counting' : 'onboarding.platforms.found';
}

/** Las cerradas se dicen donde hay un número al que sumarlas: no mientras se busca sin resultado. */
export function showsClosedCount(state) {
    return state !== COUNTING;
}

/**
 * El subtítulo son cuatro situaciones y no una: que se puede seguir sin esperar, que el número
 * todavía puede crecer, la invitación de siempre, y —con el conteo terminado en cero— qué hacer al
 * respecto. Repetir «ya podemos proponerte tus clientes» sobre cero cuentas promete un paso
 * siguiente que no existe.
 */
export function resultHintKey(state) {
    if (state === COUNTING) return 'onboarding.platforms.counting_hint';
    if (state === STILL_SEARCHING) return 'onboarding.platforms.still_searching';
    if (state === NONE) return 'onboarding.platforms.found_none_hint';
    return 'onboarding.platforms.found_hint';
}

/**
 * Sin una sola cuenta no hay cartera que armar: el paso siguiente aterrizaría en una lista vacía.
 * Con un conteo parcial sí se puede seguir: el paso 3 se reconcilia contra el servidor y lo que
 * aparezca después también entra.
 */
export function canBuildPortfolio(state) {
    return state === FOUND || state === STILL_SEARCHING;
}
